import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { Filters } from '@rossum/components/filtering';
import { FilterMultiSelect } from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';
import type { LocalizationKeys } from '../../../../i18n';
import {
  CHANGE_ORIGINS,
  type ChangeOrigin,
  FIELD_VISIBILITIES,
  type FieldRef,
  type FieldVisibility,
  HISTORY_EVENTS,
  type HistoryEvent,
  type HistoryFilters,
} from '../helpers/historyFilters';
import type { ActorRef } from '../model';

const changesLabelKeys = {
  manual: 'features.documentHistory.filters.changes.manual',
  automated: 'features.documentHistory.filters.changes.automated',
} as const satisfies Record<ChangeOrigin, LocalizationKeys>;

const eventLabelKeys = {
  fields: 'features.documentHistory.filters.event.fields',
  status: 'features.documentHistory.filters.event.status',
  queue: 'features.documentHistory.filters.event.queue',
  automationBlocker: 'features.documentHistory.filters.event.automationBlocker',
  opened: 'features.documentHistory.filters.event.opened',
} as const satisfies Record<HistoryEvent, LocalizationKeys>;

const visibilityLabelKeys = {
  visible: 'features.documentHistory.filters.visibility.visible',
  hidden: 'features.documentHistory.filters.visibility.hidden',
} as const satisfies Record<FieldVisibility, LocalizationKeys>;

type OptionOf<K extends keyof HistoryFilters> = NonNullable<
  HistoryFilters[K]
>[number];

const useLabelLookup = (options: { id: string; label: string }[]) =>
  useMemo(() => {
    const labels = R.pullObject(
      options,
      o => o.id,
      o => o.label
    );
    return (id: string) => labels[id] ?? id;
  }, [options]);

type DocumentHistoryFiltersProps = {
  filters: HistoryFilters;
  setFilters: (filters: HistoryFilters) => void;
  actors: ActorRef[];
  fields: FieldRef[];
  visibilityAvailable: boolean;
};

export const DocumentHistoryFilters = ({
  filters,
  setFilters,
  actors,
  fields,
  visibilityAvailable,
}: DocumentHistoryFiltersProps) => {
  const intl = useIntl();
  const actorOptions = useMemo(
    () => actors.map(actor => ({ id: actor.id, label: actor.name })),
    [actors]
  );
  const fieldOptions = useMemo(
    () => fields.map(field => ({ id: field.schemaId, label: field.label })),
    [fields]
  );
  const getActorLabel = useLabelLookup(actorOptions);
  const getFieldLabel = useLabelLookup(fieldOptions);

  const filterSettings = useMemo(() => {
    const enumSetting = <K extends 'changes' | 'event' | 'visibility'>({
      filterKey,
      nameKey,
      options,
      labelKeys,
    }: {
      filterKey: K;
      nameKey: LocalizationKeys;
      options: ReadonlyArray<OptionOf<K>>;
      labelKeys: Record<OptionOf<K>, LocalizationKeys>;
    }) => {
      const label = (option: string) => {
        const key = options.find(o => o === option);
        return key ? intl.formatMessage({ id: labelKeys[key] }) : option;
      };
      return createSettings({
        filterKey,
        options: [...options],
        optionsAreLoading: false,
        name: intl.formatMessage({ id: nameKey }),
        getOptionLabel: label,
        getChipLabel: (value: OptionOf<K>[]) => value.map(label).join(', '),
        valueGetter: (current: HistoryFilters) => current[filterKey],
        setFilter: (value: HistoryFilters[K]) =>
          setFilters({ ...filters, [filterKey]: value }),
        Component: FilterMultiSelect,
      });
    };

    const derivedSetting = ({
      filterKey,
      nameKey,
      options,
      getLabel,
    }: {
      filterKey: 'changedBy' | 'field';
      nameKey: LocalizationKeys;
      options: { id: string; label: string }[];
      getLabel: (option: string) => string;
    }) =>
      createSettings({
        filterKey,
        options: options.map(o => o.id),
        optionsAreLoading: false,
        name: intl.formatMessage({ id: nameKey }),
        getOptionLabel: getLabel,
        getChipLabel: (value: string[]) => value.map(getLabel).join(', '),
        valueGetter: (current: HistoryFilters) => current[filterKey],
        setFilter: (value: string[] | undefined) =>
          setFilters({ ...filters, [filterKey]: value }),
        Component: FilterMultiSelect,
      });

    return [
      enumSetting({
        filterKey: 'changes',
        nameKey: 'features.documentHistory.filters.changes',
        options: CHANGE_ORIGINS,
        labelKeys: changesLabelKeys,
      }),
      derivedSetting({
        filterKey: 'changedBy',
        nameKey: 'features.documentHistory.filters.changedBy',
        options: actorOptions,
        getLabel: getActorLabel,
      }),
      enumSetting({
        filterKey: 'event',
        nameKey: 'features.documentHistory.filters.event',
        options: HISTORY_EVENTS,
        labelKeys: eventLabelKeys,
      }),
      derivedSetting({
        filterKey: 'field',
        nameKey: 'features.documentHistory.filters.field',
        options: fieldOptions,
        getLabel: getFieldLabel,
      }),
      ...(visibilityAvailable
        ? [
            enumSetting({
              filterKey: 'visibility',
              nameKey: 'features.documentHistory.filters.visibility',
              options: FIELD_VISIBILITIES,
              labelKeys: visibilityLabelKeys,
            }),
          ]
        : []),
    ];
  }, [
    intl,
    filters,
    setFilters,
    actorOptions,
    getActorLabel,
    fieldOptions,
    getFieldLabel,
    visibilityAvailable,
  ]);

  return (
    <Filters
      dataCy="document-history-filters"
      filters={filters}
      setFilters={next => setFilters(next ?? {})}
      filterSettings={filterSettings}
    />
  );
};
